import * as _ from "lodash";

import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { catchError, filter, finalize, switchMap, take } from "rxjs/operators";

import { Injectable } from "@angular/core";
import { StorageService } from "../services/storage.service";
import { SpinnerServiceForUserPromptService } from "../services/spinner-service-for-user-prompt.service";
import { NgxSpinnerService } from "ngx-spinner";
import { SsoToken } from "src/app/services/models/response-models/sso-token.response-model";
import { AuthService } from "src/app/services/auth.service";
import { AppConstants } from "../constants/app-constants";
import { Router } from "@angular/router";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  isRefreshing = false;
  private failedTokenRequest = false;
  private totalRequests = 0;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  private loaderTimeout: any;

  constructor(
    private storageService: StorageService,
    private spinner: NgxSpinnerService,
    private authService: AuthService,
    private spinnerServiceForUserPrompt: SpinnerServiceForUserPromptService,
    private router: Router
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const forceSpinner = req.headers.has(AppConstants.forceSpinner);

    const skipSpinner =
      !forceSpinner &&
      (req.headers.has(AppConstants.skipSpinner) ||
        (this.router.url.startsWith("/home") && req.method === "GET"));

    const newReq = req.clone({
      headers: req.headers
        .delete(AppConstants.skipSpinner)
        .delete(AppConstants.forceSpinner),
    });

    if (!skipSpinner) {
      this.totalRequests++;
      // Clear any pending hide operations
      clearTimeout(this.loaderTimeout);

      // Show spinner immediately for new requests
      if (this.totalRequests === 1) {
        this.spinner.show();
      }
    }

    return next
      .handle(this.addTokenHeader(req, this.storageService.getAccessToken()!))
      .pipe(
        catchError((error: any) => {
          if (error.status === 401 && !newReq.url.includes("token")) {
            return this.handle401Error(newReq, next);
          }
          // commented the code for handling 403 error as it is not required and creating issue in documentation
          // if(error.status === 403){
          //   this.authService.handleForbiddenCall();
          //   console.log(error);
          //   return of(error);
          // }
          if (newReq.url.includes("token")) {
            this.spinner.hide();
            this.authService.reAuthenticate();
          }

          return throwError(() => error);
        }),
        finalize(() => {
          if (!skipSpinner) {
            this.totalRequests--;
            if (this.totalRequests === 0) {
              // Add a small delay before hiding to ensure smooth transition
              this.loaderTimeout = setTimeout(() => {
                this.spinner.hide();
                if (
                  !this.spinnerServiceForUserPrompt
                    .getSpinnerSubjectStatus()
                    .valueOf()
                ) {
                  this.spinnerServiceForUserPrompt.setSpinnerSubjectHidden();
                }
              }, 50); // Minimal delay to prevent flickering
            }
          }
        })
      );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const tokenKey = sessionStorage.getItem("access_token");
      const refreshToken = sessionStorage.getItem("refresh_token");

      if (tokenKey && refreshToken) {
        const data = _.merge(tokenKey, {
          refreshToken: refreshToken,
        });
        if (data) {
          return this.authService.getRefreshToken().pipe(
            switchMap((response: SsoToken) => {
              this.isRefreshing = false;

              sessionStorage.setItem("access_token", response.access_token);
              sessionStorage.setItem("refresh_token", response.refresh_token);
              this.refreshTokenSubject.next(response.access_token);

              return next.handle(
                this.addTokenHeader(request, response.access_token)
              );
            }),
            catchError((err) => {
              this.isRefreshing = false;
              if (err.url.includes("token")) {
                if (!this.failedTokenRequest) {
                  this.spinner.hide();
                  this.authService.reAuthenticate();
                }
                this.failedTokenRequest = true;
              }
              return throwError(() => new Error(err));
            })
          );
        }
      } else {
        this.spinner.hide();
        this.authService.logout();
      }
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({
      headers: request.headers.set("Authorization", `Bearer ${token}`),
    });
  }
}
